import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import { Navigate, useNavigate } from 'react-router-dom';
import { CircularProgress, Container } from '@mui/material';
import { useAppDispatch, useAppSelector } from "../../Config/Hooks";
import { login, logout } from '../../Config/Reducers/AuthenticationReducer';
import { useWebSocket } from './hooks/useWebSocket';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { resolveHomeRoute } from '../../Utils/HomeScreenUtils';

const Login: React.FC = () => {
    const { enqueueSnackbar } = useSnackbar();

    const auth = useAuth();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const isAuthenticated = useAppSelector((state) => state.authentication.isAuthenticated);
    const account = useAppSelector((state) => state.authentication.account);
    const loginError = useAppSelector(state => state.authentication.loginError);
    const errorMessage = useAppSelector(state => state.authentication.errorMessage);
    const tokenError = auth.error?.message.includes("Invalid");

    const [customerName, setCustomerName] = useState<string | null>(null);
    const { sendMessage, hostname } = useWebSocket();

    const handleError = useCallback((msg: string) => {
        console.log("Enviando erro via WebSocket:", msg);
        if(sendMessage && auth.user?.profile.preferred_username) {
            sendMessage({
                email: auth.user.profile.preferred_username,
                hostname: hostname,
                customerName: customerName,
                action: "login",
                message: msg,
                status: "ERROR",
                timeStamp: new Date().toISOString(),
            });
        }
    }, [sendMessage, hostname, customerName, auth.user?.profile.preferred_username]);

    const fetchCustomerName = useCallback(async (email: string) => {
        try {
            const response = await axios.get("/api/customer/find-by-email", {
                params: { email: email },
            });
            if(response?.status === 200){
                const customerName = response.data.data ? String(response.data.data) : null;
                setCustomerName(customerName);
            } else {
                setCustomerName(null);
            }
        } catch (error) {
            setCustomerName(null);
        }
    }, []);

    useEffect(() => {
        console.log('Login - Auth State:', {
            isLoading: auth.isLoading,
            isAuthenticated: auth.isAuthenticated,
            hasUser: !!auth.user,
            hasError: !!auth.error,
            errorMessage: auth.error?.message,
            currentUrl: window.location.href
        });

        if (auth.isLoading) {
            console.log('Login - Still loading...');
            return;
        }

        // Handle token errors
        if (tokenError) {
            console.error('Login - Token error detected:', auth.error);
            auth.signoutRedirect();
            return;
        }

        // Handle unauthenticated state
        if (!auth.isAuthenticated && !tokenError) {
            const currentUrl = window.location.href;
            const hasCode = currentUrl.includes('code=');
            const hasError = currentUrl.includes('error=');
            
            console.log('Login - Unauthenticated state:', {
                hasCode,
                hasError,
                currentUrl
            });

            // Only redirect if we're not already in the process of authentication
            if (!hasCode && !hasError) {
                console.log('Login - Initiating signin redirect');
                auth.signinRedirect();
            } else if (auth.error?.message?.includes('No matching state found in storage')) {  // Handle state mismatch error
                console.log('Login - State mismatch error detected, redirecting to origin');
                window.location.href = window.location.origin;
            }
            return;
        }

        // Handle successful authentication
        if (auth.user?.profile.email) {
            console.log('Login - Successful authentication, fetching customer data');
            setCustomerName(null);
            fetchCustomerName(auth.user.profile.email);
            dispatch(login(auth));
        }
    }, [auth.isLoading, auth.isAuthenticated, tokenError, auth.user?.profile.email, dispatch, navigate, fetchCustomerName]);

    useEffect(() => {
        if (!loginError && !tokenError) return;

        console.log('Login - Error handling:', {
            loginError,
            tokenError,
            errorMessage
        });

        let msg = "";
        if (errorMessage?.toLowerCase().includes("timeout") || errorMessage?.toLowerCase().includes("network")) {
            msg = "Falha de comunicação!";
        } else if (errorMessage?.toLowerCase().includes("500")) {
            msg = "Erro no servidor. Contate o suporte.";
        } else if (errorMessage?.toLowerCase().includes("503")) {
            msg = "Serviço de login indisponível.";
        } else if (errorMessage?.toLowerCase().includes("204")) {
            msg = "Usuário não encontrado.";
        } else if (errorMessage?.toLowerCase().includes("401")) {
            msg = "Usuário ou senha inválidos.";
        } else {
            msg = errorMessage || "Erro ao logar!";
        }

        if (msg) {
            enqueueSnackbar(msg, { variant: 'error' });
            handleError(msg);
            
            // Only logout and redirect if it's a critical error            
            dispatch(logout());
            setTimeout(() => {
                auth.signoutRedirect();
            }, 2000);
        }
    }, [loginError, tokenError, errorMessage, enqueueSnackbar, handleError, dispatch]);

    if (isAuthenticated === true && account && account?.name !== undefined && account?.name !== null && account?.name.length > 0) {
        return <Navigate to={resolveHomeRoute(account.homeScreen, account.authoritySet)} />;
    }

    return (
        <Container maxWidth="xs" id="login-form" style={{
            textAlign: "center"
        }}>
            <CircularProgress 
                style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)'
                }} 
            />    
        </Container>
    );
};

export default Login; 